import { useEffect, useState } from 'react';
import { Badge } from 'antd';

import { useGetAllReferencesQuery, useLazyGetAllReferencesQuery } from 'app/services/edpApiV1/references';

import { dataForReferencesTable, sortingDate } from 'shared/helpers/utils';
import { RequestsSearch, TColumns } from 'shared/types/types';

import { TableArchive } from 'features/tables';
import { SortCondition } from 'shared/enums';
import { usePullSort } from 'shared/hooks';
import { TPreparedReferencesTableData } from 'shared/types/reference.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';

const ReferencesArchive = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataReferences, setDataReferences] = useState<TPreparedReferencesTableData[] | []>([]);
  const [sortInfo, sortTrigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  const [getReferences, { data, isFetching, isSuccess }] = useLazyGetAllReferencesQuery();

  useEffect(() => {
    getReferences({
      type: 'myArchive',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getReferences, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  useEffect(() => {
    if (data) {
      const dataTable = dataForReferencesTable(data?.data);
      if (dataTable) {
        setDataReferences(dataTable);
      }
    }
  }, [data]);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  return (
    <>
      {dataReferences && (
        <TableArchive
          dataSource={dataReferences}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={data?.pagination?.total}
          tabKey="myArchive"
          onSearch={onSearch}
        />
      )}
    </>
  );
};

export default ReferencesArchive;
